import { useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { CBDatePicker } from "../shared";
import SectionTitle from "../shared/sectionTitle/sectionTitle";
import { useForm } from "react-hook-form";
import { reportsSchema, reportsValues, setFiltersRender } from "./utils";
import { FileExcelOutlined, FilePdfOutlined } from "@ant-design/icons";
import CustomCard from "../shared/CustomCard/CustomCard";
import {
  Button,
  Radio,
  RadioChangeEvent,
  Select,
  Spin,
  Switch,
  Typography,
} from "antd";
import { useBuildReport } from "./useBuildReport";
import "./reports.scss";
import ProgramCharts from "./components/programCharts";
import CourseChart from "./components/courseCharts";
import { usePDF } from "react-to-pdf";
import * as XLSX from "xlsx";
import { Controller } from "react-hook-form";
import noData from "./components/noData";
import StudentCharts from "./components/studentCharts";
import { useDimensions } from "../../hooks/useDimensions";
import { T } from "../../interfaces/survey.interface";

const Reports = () => {
  const [actionLoader, setActionLoader] = useState<boolean>(false);
  const { toPDF, targetRef } = usePDF({
    filename: "SEABC-Report.pdf",
    page: { margin: 5 },
  });
  const { width } = useDimensions();
  const [params, setParams] = useState<any>({});
  const { Text } = Typography;
  const { control, watch, setValue } = useForm({
    mode: "onBlur",
    resolver: yupResolver(reportsSchema),
    defaultValues: reportsValues(),
    values: reportsValues(),
  });

  const {
    isLoading,
    allCourses,
    allStudents,
    programReport,
    coursesReport,
    studentsReport,
  } = useBuildReport(watch("selection")!, params);

  const onChangeSelection = (e: RadioChangeEvent) => {
    setValue("selection", e.target.value, { shouldDirty: true });
  };

  const onChangeFilterOption = (check: boolean) => {
    setValue("filter", check, { shouldDirty: true });
    setValue("from", "", { shouldDirty: true });
    setValue("to", "", { shouldDirty: true });
  };

  const onChangeRange = (range: string[]) => {
    setValue("from", range[0], { shouldDirty: true });
    setValue("to", range[1], { shouldDirty: true });
  };

  const yearsOptions = new Array(10)
    .fill(new Date().getFullYear())
    .map((year, i) => year - i)
    .map((year) => year.toString())
    .map((stringifiedYear) => ({
      value: stringifiedYear,
      label: stringifiedYear,
    }));

  const exportToExcel = () => {
    setActionLoader(true);
    const workbook = XLSX.utils.book_new();
    let obj =
      watch("selection") === "Program"
        ? programReport
        : watch("selection") === "Course"
          ? coursesReport
          : watch("selection") === "Student"
            ? studentsReport
            : [];
    if (watch("selection") === "Student") delete obj.averageFinalGrade;

    obj = setFiltersRender(
      obj,
      watch(),
      watch("selection") === "Student" &&
        allStudents?.find((s) => s.id === Number(watch("student"))),
    );

    for (const [sheetName, sheetData] of Object.entries(obj)) {
      const worksheet = XLSX.utils.json_to_sheet(sheetData as any);
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    }

    XLSX.writeFile(workbook, "exported_report_data.xlsx");
    setActionLoader(false);
  };

  const generateReport = () => {
    setParams({
      ...(watch("filter") &&
        watch("from") &&
        watch("from") !== "Invalid date" && {
          startDate: watch("from"),
          endDate: watch("to"),
        }),
      ...(!watch("filter") && { term: watch("term"), year: watch("year") }),
      ...(watch("selection") === "Course" &&
        watch("course") && { courses: watch("course") }),
      ...(watch("selection") === "Student" &&
        watch("student") && { student: watch("student") }),
    });
  };

  const checkValidation = () => {
    if (watch("selection") === "Program" && programReport) return false;
    else if (watch("selection") === "Course" && coursesReport) return false;
    else if (watch("selection") === "Student" && studentsReport) return false;
    else return true;
  };

  return (
    <>
      {window.location.href.includes("reports") && (
        <SectionTitle sectionName="Reports & Analytics" />
      )}
      <div
        className="flex flex-col justify-center max-w-6xl p-4"
        style={{ margin: "0 auto" }}
        ref={targetRef}
      >
        <CustomCard title="Report Generator">
          <div
            className={`flex ${width <= 500 ? "flex-wrap gap-16" : "gap-24"} justify-center pb-4`}
            style={{ margin: `0rem ${width >= 500 ? "5rem" : "3rem"}` }}
          >
            <div className="">
              <div className="pb-4">
                <Text>Type History</Text>
              </div>
              <div className="pl-2">
                <Radio.Group
                  style={{ transform: "scale(1.1)" }}
                  onChange={onChangeSelection}
                  value={watch("selection")}
                >
                  <Radio.Button value="Program">Program</Radio.Button>
                  <Radio.Button value="Course">Course</Radio.Button>
                  <Radio.Button value="Student">Student</Radio.Button>
                </Radio.Group>
              </div>
            </div>
            <div
              className="flex flex-1 gap-8"
              style={{ minWidth: width >= 500 ? "25rem" : undefined }}
            >
              <div className="flex-1">
                <div className="pl-2">
                  <Switch
                    style={{ transform: "scale(1.2)" }}
                    checkedChildren="Range Dates"
                    unCheckedChildren="Terms"
                    value={watch("filter")}
                    onChange={onChangeFilterOption}
                  />
                </div>
                {watch("filter") ? (
                  <div>
                    <CBDatePicker
                      range={true}
                      name="ranges"
                      control={control}
                      onChange={onChangeRange}
                    />
                  </div>
                ) : (
                  <div className="flex gap-4" style={{ paddingTop: "0.6rem" }}>
                    <div
                      className={`flex flex-1 gap-1 items-center`}
                      style={{ minWidth: width >= 500 ? undefined : "6rem" }}
                    >
                      <div>
                        <Text>Year:</Text>
                      </div>
                      <div className="flex-1">
                        <Controller
                          control={control}
                          name="year"
                          render={({ field }) => (
                            <Select
                              mode="multiple"
                              maxTagCount="responsive"
                              placeholder="Year"
                              size="large"
                              allowClear
                              style={{
                                flex: 1,
                                width: "-webkit-fill-available",
                              }}
                              onChange={(value) => field.onChange(value)}
                              options={yearsOptions}
                            />
                          )}
                        />
                      </div>
                    </div>
                    <div
                      className="flex flex-2 gap-1 items-center"
                      style={{ flex: 2 }}
                    >
                      <div>
                        <Text>Term:</Text>
                      </div>
                      <Controller
                        control={control}
                        name="term"
                        render={({ field }) => (
                          <Select
                            mode="multiple"
                            maxTagCount="responsive"
                            placeholder="Term"
                            size="large"
                            allowClear
                            style={{ flex: 1 }}
                            onChange={(value) => field.onChange(value)}
                            options={[
                              {
                                value: "Term 1: September",
                                label: "Term 1: September",
                              },
                              {
                                value: "Term 2: January",
                                label: "Term 2: January",
                              },
                              {
                                value: "Term 3: April",
                                label: "Term 3: April",
                              },
                            ]}
                          />
                        )}
                      />
                    </div>
                  </div>
                )}
                {watch("selection") === "Course" ? (
                  <div
                    className={`flex items-center gap-2 ${!watch("filter") ? "pt-2" : undefined}`}
                  >
                    <Text>Courses:</Text>
                    <Controller
                      control={control}
                      name="course"
                      render={({ field }: any) => (
                        <Select
                          mode="multiple"
                          size="large"
                          key="coursesLists"
                          maxTagCount="responsive"
                          allowClear
                          style={{ flex: 1 }}
                          placeholder="Please select"
                          onChange={(value) => field.onChange(value)}
                          options={allCourses}
                        />
                      )}
                    />
                  </div>
                ) : watch("selection") === "Student" ? (
                  <div
                    className={`flex items-center gap-2 ${!watch("filter") ? "pt-2" : undefined}`}
                  >
                    <Text>Student:</Text>
                    <Controller
                      control={control}
                      name="student"
                      render={({ field }: any) => (
                        <Select
                          size="large"
                          showSearch
                          key="studentsLists"
                          allowClear
                          style={{ flex: 1 }}
                          placeholder="Please select"
                          onChange={(_, student: T) =>
                            field.onChange(student?.id)
                          }
                          options={allStudents}
                        />
                      )}
                    />
                  </div>
                ) : null}
              </div>
            </div>
            <div className="flex gap-8">
              <div>
                <div className="pb-2">
                  <Text>Download</Text>
                </div>
                <div className="flex gap-2 pb-3">
                  <Button
                    type="text"
                    size="large"
                    icon={<FilePdfOutlined />}
                    loading={actionLoader}
                    disabled={checkValidation()}
                    onClick={() => toPDF()}
                  >
                    PDF
                  </Button>
                  <Button
                    type="text"
                    size="large"
                    icon={<FileExcelOutlined />}
                    loading={actionLoader}
                    disabled={checkValidation()}
                    onClick={() => exportToExcel()}
                  >
                    Excel
                  </Button>
                </div>
                <Button
                  block
                  size="large"
                  type="primary"
                  loading={isLoading}
                  onClick={() => generateReport()}
                >
                  Create Report
                </Button>
              </div>
            </div>
          </div>
        </CustomCard>
        {isLoading && <Spin size="large" fullscreen />}
        <div className="flex gap-12 pt-2">
          {watch("selection") === "Program" ? (
            <div ref={targetRef} className="flex-1">
              <ProgramCharts data={programReport} />
            </div>
          ) : watch("selection") === "Course" ? (
            <div className="flex-1">
              {watch("course")?.length && coursesReport ? (
                <div ref={targetRef} className="flex-1">
                  <CourseChart data={coursesReport} />
                </div>
              ) : (
                <>{noData(false, true)}</>
              )}
            </div>
          ) : watch("selection") === "Student" ? (
            <div ref={targetRef} className="flex-1">
              {studentsReport ? (
                <StudentCharts
                  params={params}
                  data={studentsReport}
                  student={watch("student")!}
                />
              ) : (
                <>{noData(false, false)}</>
              )}
            </div>
          ) : (
            <>{noData(false)}</>
          )}
        </div>
      </div>
    </>
  );
};

export default Reports;
